import type { AdminPermissionModel, AdminUserRoles } from "@/api/orval/model";
import { getGetAdminPermissionsQueryOptions, useGetAdminPermissions } from "@/api/orval/auth/auth";
import { queryClient } from "@/core/caching/queryClient";

import { isAuthorized } from "./token";

export const checkIsMultiPlatformUser = (permissions: { roles: string[] }[]) =>
  permissions.length > 1 ||
  permissions.some(({ roles }) => roles.includes("RepeatMDAdministrator"));

export type UserPermissions = AdminPermissionModel & {
  rolesSet: Set<AdminUserRoles>;
  isStaffUser: boolean;
  isManagerUser: boolean;
  isMarketingUser: boolean;
  isAdministratorUser: boolean;
  isAgelessStaffUser: boolean;
  isAgelessManagerUser: boolean;
  isAgelessMarketingUser: boolean;
  isAgelessAdministratorUser: boolean;
  isAgelessContactManagerUser: boolean;
  isAgelessCommunicationManagerUser: boolean;
  isAgelessContactOwnerAssignerUser: boolean;
  isAgelessAssignedContactsOnlyUser: boolean;
  isAgelessReportViewerUser: boolean;
  isAgelessNotificationReceiverUser: boolean;
  isAgelessBillingManagerUser: boolean;
  isAgelessAdsManagerUser: boolean;
  isAgelessLeadEnrichmentViewerUser: boolean;
  isAgelessImageReviewerUser: boolean;
  isAgelessImageReviewSupervisorUser: boolean;
  isFinanceUser: boolean;
  isClientManagerUser: boolean;
  isCustomPlanManagerUser: boolean;
  isCommunicationManagerUser: boolean;
  isRepeatMDAdministratorUser: boolean;
  isRepeatMDExecutiveUser: boolean;
  isRepeatMDEngineeringUser: boolean;
  isRepeatMDProductUser: boolean;
  isRepeatMDMarketingUser: boolean;
  isRepeatMDSalesUser: boolean;
  isRepeatMDCustomerExperienceUser: boolean;
  isRefundManagerUser: boolean;
  isMultiPlatformUser: boolean;
  isClientMembershipManagerUser: boolean;
  canAccessAgelessMessaging: boolean;
  canAccessAgelessContacts: boolean;
  canViewAgelessReporting: boolean;
  canManageAgelessBilling: boolean;
  canManageAgelessAds: boolean;
  canViewAgelessLeadEnrichment: boolean;
  // Mirrors AgelessImageReviewController: reviewer or supervisor may review images and see
  // their own feedback.
  canReviewAgelessImages: boolean;
  // Mirrors AgelessImageReviewSupervisorController: supervisor-only dashboard, all feedback,
  // generations, and the model change log.
  canSuperviseAgelessImageReview: boolean;
};

const fetchPermissions = (): Promise<AdminPermissionModel[]> => {
  return queryClient.fetchQuery(
    getGetAdminPermissionsQueryOptions({ query: { staleTime: Infinity, gcTime: Infinity } }),
  );
};

export const enhancePermissions = (
  permissions: AdminPermissionModel,
  _: number,
  allPermissions: AdminPermissionModel[],
): UserPermissions => {
  const rolesSet = new Set(permissions.roles);
  const hasRole = (role: string) => rolesSet.has(role as AdminUserRoles);

  const isAgelessContactManagerUser = hasRole("AgelessContactManager");
  const isAgelessCommunicationManagerUser = hasRole("AgelessCommunicationManager");
  const isAgelessContactOwnerAssignerUser = hasRole("AgelessContactOwnerAssigner");
  // TODO: AgelessAssignedContactsOnly should filter the contacts/communications
  // lists to show only assigned contacts. The role is stored and surfaced here
  // but the actual query-level filtering is not yet implemented (backend + frontend).
  const isAgelessAssignedContactsOnlyUser = hasRole("AgelessAssignedContactsOnly");
  const isAgelessReportViewerUser = hasRole("AgelessReportViewer");
  const isAgelessNotificationReceiverUser = hasRole("AgelessNotificationReceiver");
  const isAgelessBillingManagerUser = hasRole("AgelessBillingManager");
  const isAgelessAdsManagerUser = hasRole("AgelessAdsManager");
  const isAgelessLeadEnrichmentViewerUser = hasRole("AgelessLeadEnrichmentViewer");
  const isAgelessImageReviewerUser = hasRole("AgelessImageReviewer");
  const isAgelessImageReviewSupervisorUser = hasRole("AgelessImageReviewSupervisor");

  const isAgelessStaffUser = hasRole("AgelessStaff");
  const isAgelessManagerUser = hasRole("AgelessManager");
  const isAgelessAdministratorUser = hasRole("AgelessAdministrator");
  const isRepeatMDAdministratorUser = rolesSet.has("RepeatMDAdministrator");
  const isAdministratorUser = hasRole("Administrator");

  return {
    ...permissions,
    rolesSet,
    isStaffUser: hasRole("Staff"),
    isManagerUser: hasRole("Manager"),
    isMarketingUser: hasRole("Marketing"),
    isAdministratorUser,
    isAgelessStaffUser,
    isAgelessManagerUser,
    isAgelessMarketingUser: hasRole("AgelessMarketing"),
    isAgelessAdministratorUser,
    isAgelessContactManagerUser,
    isAgelessCommunicationManagerUser,
    isAgelessContactOwnerAssignerUser,
    isAgelessAssignedContactsOnlyUser,
    isAgelessReportViewerUser,
    isAgelessNotificationReceiverUser,
    isAgelessBillingManagerUser,
    isAgelessAdsManagerUser,
    isAgelessLeadEnrichmentViewerUser,
    isAgelessImageReviewerUser,
    isAgelessImageReviewSupervisorUser,
    isFinanceUser: rolesSet.has("Finance"),
    isClientManagerUser: rolesSet.has("ClientManager"),
    isCustomPlanManagerUser: rolesSet.has("CustomPlanManager"),
    isCommunicationManagerUser: rolesSet.has("CommunicationManager"),
    isRepeatMDAdministratorUser,
    isRepeatMDExecutiveUser: rolesSet.has("RepeatMDExecutive"),
    isRepeatMDEngineeringUser: rolesSet.has("RepeatMDEngineering"),
    isRepeatMDProductUser: rolesSet.has("RepeatMDProduct"),
    isRepeatMDMarketingUser: rolesSet.has("RepeatMDMarketing"),
    isRepeatMDSalesUser: rolesSet.has("RepeatMDSales"),
    isRepeatMDCustomerExperienceUser: rolesSet.has("RepeatMDCustomerExperience"),
    isRefundManagerUser: rolesSet.has("RefundManager"),
    isClientMembershipManagerUser: rolesSet.has("ClientMembershipManager"),
    canAccessAgelessMessaging:
      isAgelessCommunicationManagerUser ||
      hasRole("CommunicationManager") ||
      isAdministratorUser ||
      isRepeatMDAdministratorUser,
    canAccessAgelessContacts:
      isAgelessContactManagerUser || isAdministratorUser || isRepeatMDAdministratorUser,
    canViewAgelessReporting:
      isAgelessReportViewerUser || isAdministratorUser || isRepeatMDAdministratorUser,
    // Mirrors AgelessSettingsBillingController's role allowlist on the backend.
    // Keep these in sync; the controller is the source of truth for "who can
    // read/write Ageless billing state".
    canManageAgelessBilling:
      isAgelessBillingManagerUser ||
      isAgelessAdministratorUser ||
      isAdministratorUser ||
      isRepeatMDAdministratorUser ||
      rolesSet.has("RepeatMDExecutive") ||
      rolesSet.has("RepeatMDEngineering") ||
      rolesSet.has("RepeatMDProduct"),
    // Mirrors MetaAdsController.AllowedRoles on the backend (AgelessAdsManager |
    // AgelessAdministrator | Administrator | RepeatMDAdministrator). Keep these in sync; the
    // controller is the source of truth for "who can connect/manage a platform's Meta Ads account".
    canManageAgelessAds:
      isAgelessAdsManagerUser ||
      isAgelessAdministratorUser ||
      isAdministratorUser ||
      isRepeatMDAdministratorUser,
    // Gates income / job-title enrichment in the UI and the income-demographics API
    // (see AgelessAnalyticsController.GetIncomeDemographics). RepeatMD admins also
    // pass the backend allowlist; platform admins without this role still access
    // Home and contacts with enrichment fields omitted.
    canViewAgelessLeadEnrichment: isAgelessLeadEnrichmentViewerUser || isRepeatMDAdministratorUser,
    // RepeatMD admins pass the backend gate through the global-admin bypass, so they get the
    // supervisor surface too; reviewer-only rows (external advisors) stop at the review queue.
    canReviewAgelessImages:
      isAgelessImageReviewerUser ||
      isAgelessImageReviewSupervisorUser ||
      isRepeatMDAdministratorUser,
    canSuperviseAgelessImageReview:
      isAgelessImageReviewSupervisorUser || isRepeatMDAdministratorUser,
    isMultiPlatformUser: checkIsMultiPlatformUser(allPermissions),
  };
};

export const getPlatformPermissions = async (
  targetPlatformId: string,
): Promise<UserPermissions> => {
  const permissions = await fetchPermissions();
  const enhancedPermissions = permissions.map((permission, idx) =>
    enhancePermissions(permission, idx, permissions),
  );

  const targetPermission = enhancedPermissions.find(
    ({
      isRepeatMDAdministratorUser,
      isRepeatMDEngineeringUser,
      isRepeatMDProductUser,
      platformId,
    }) =>
      isRepeatMDAdministratorUser ||
      isRepeatMDEngineeringUser ||
      isRepeatMDProductUser ||
      platformId === targetPlatformId,
  );
  if (!targetPermission) {
    throw new Error(`No permissions found for platform ${targetPlatformId}`);
  }

  return targetPermission;
};

export const getAllPermissions = async (): Promise<UserPermissions[]> => {
  const permissions = await fetchPermissions();

  return permissions.map((permission, idx) => enhancePermissions(permission, idx, permissions));
};

export const usePermissions = (): UserPermissions[] => {
  const authorized = isAuthorized();

  const { data } = useGetAdminPermissions({
    query: { staleTime: Infinity, gcTime: Infinity, enabled: authorized },
  });

  return data?.map(enhancePermissions) ?? [];
};

export function checkIsRepeatMDAdmin(
  permissions: Array<{ roles?: unknown; isRepeatMDAdministratorUser?: unknown }>,
): boolean {
  return permissions.some((permission) => {
    const roles = Array.isArray(permission.roles) ? permission.roles : [];
    return (
      permission.isRepeatMDAdministratorUser === true || roles.includes("RepeatMDAdministrator")
    );
  });
}

const AGELESS_FALLBACK_ROUTES: Array<{
  key: keyof Pick<
    UserPermissions,
    | "canViewAgelessReporting"
    | "canAccessAgelessContacts"
    | "canAccessAgelessMessaging"
    | "canManageAgelessBilling"
    | "isAgelessAdministratorUser"
    | "isAgelessManagerUser"
    | "isAgelessStaffUser"
  >;
  path: string;
}> = [
  { key: "canViewAgelessReporting", path: "ageless/home" },
  { key: "canAccessAgelessContacts", path: "ageless/contacts" },
  { key: "canAccessAgelessMessaging", path: "ageless/messaging" },
  // AgelessAdministrators now also satisfy `canManageAgelessBilling` (mirroring
  // the backend controller). Keep this rule ahead of the billing fallback so
  // they continue to land on the settings root instead of the billing subpage.
  { key: "isAgelessAdministratorUser", path: "ageless/settings" },
  { key: "canManageAgelessBilling", path: "ageless/settings/billing" },
  { key: "isAgelessManagerUser", path: "ageless/catalog" },
  { key: "isAgelessStaffUser", path: "ageless/university" },
];

export function resolveAgelessFallbackPath(permissions: UserPermissions): string | null {
  const target = AGELESS_FALLBACK_ROUTES.find((r) => permissions[r.key]);
  return target?.path ?? null;
}

export function hasAgelessOnboardingWizardAccess(permissions: UserPermissions): boolean {
  return (
    permissions.isAgelessManagerUser ||
    permissions.isAgelessAdministratorUser ||
    permissions.isAdministratorUser ||
    permissions.isRepeatMDAdministratorUser
  );
}

export const canChangePatientPhoneNumber = (
  permissions: Pick<
    UserPermissions,
    "isAdministratorUser" | "isClientManagerUser" | "isRepeatMDAdministratorUser"
  >,
): boolean =>
  permissions.isAdministratorUser ||
  permissions.isClientManagerUser ||
  permissions.isRepeatMDAdministratorUser;
